import { useMemo } from 'react';

import { ALL_OPTION, OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core';
import { useOrganizationOverridableProperties } from '@/modules/core/hooks/useOrganizationOverridableProperties';
import { IFileTypeDtoFileTypeEnum } from '@/types';

import { ADJUSTMENT_OPTION, FILE_RECORD_TYPES_OPTIONS } from '../FileManager.constants';

import useAdjustmentBalanceFeatureFlag from './useAdjustmentBalanceFeatureFlag';

const useAllowedFileTypes = () => {
  const { observingOrganization } = useCurrentOrganization();

  const { observingOrganizationData } = useOrganizationOverridableProperties();
  const isSystemLevel = useMemo(
    () => observingOrganization.type === OrganizationTypes.system,
    [observingOrganization]
  );

  const allowedFileTypes = useMemo(
    () => observingOrganizationData?.allowedFileTypes,
    [observingOrganizationData?.allowedFileTypes]
  );

  const hasOnlyOneFileTypeAllowed = useMemo(() => allowedFileTypes?.length === 1, [allowedFileTypes]);
  const isAdjustmentBalanceAvailable = useAdjustmentBalanceFeatureFlag();

  const allowedFileRecordTypesOptions = useMemo(() => {
    const allowedFileTypesUpper = allowedFileTypes || [];
    if (isSystemLevel) {
      if (!isAdjustmentBalanceAvailable) {
        return FILE_RECORD_TYPES_OPTIONS.filter((item) => item.key !== ADJUSTMENT_OPTION.key);
      }
      return FILE_RECORD_TYPES_OPTIONS;
    }
    const filteredFileTypes = FILE_RECORD_TYPES_OPTIONS.filter((fileType) =>
      allowedFileTypesUpper.includes(fileType.key as IFileTypeDtoFileTypeEnum)
    );
    const allOption = filteredFileTypes.length > 1 ? [ALL_OPTION] : [];
    const list = [...allOption, ...filteredFileTypes];

    if (!isAdjustmentBalanceAvailable) {
      return list.filter((item) => item.key !== ADJUSTMENT_OPTION.key);
    }

    return list;
  }, [allowedFileTypes, isAdjustmentBalanceAvailable, isSystemLevel]);

  const currentAllowedFileTypes = useMemo(() => {
    const filtered = allowedFileRecordTypesOptions
      .filter((item) => item.key !== ALL_OPTION.key)
      .map((item) => item.key) as IFileTypeDtoFileTypeEnum[];

    return filtered;
  }, [allowedFileRecordTypesOptions]);
  const isAdjustmentAllowed = useMemo(() => {
    return allowedFileRecordTypesOptions.some((item) => item.key == ADJUSTMENT_OPTION.key);
  }, [allowedFileRecordTypesOptions]);

  return {
    isAdjustmentAllowed,
    allowedFileRecordTypesOptions,
    hasOnlyOneFileTypeAllowed,
    currentAllowedFileTypes,
    allowedFileTypes,
  };
};

export default useAllowedFileTypes;
